import { useCallback } from 'react';
import assocPath from 'ramda/es/assocPath';
import clone from 'ramda/es/clone';
import { useSetRecoilState } from 'recoil';
import type { CallbackInterface } from 'recoil';
import {
  steppedHPConfigurations,
  clinicalStepsBaseline,
  hangingProtocolStepIndex,
  hangingProtocolStepIndexAttributed,
  hangingProtocolSubStepIndex,
  makeVersionedSteps,
  type StepsUpdater,
  type VersionedStepConfigurations,
} from './state';
import type { StepConfiguration, StepConfigurationInput } from './steppedHPTypes';
import { endPendingStepCreate, isPendingStepWrite } from './pendingStepMutations';
import { traceHPMutation } from './hpMutationTrace';
import type { DehydratedViewportsConfigurations } from '../ViewportsConfigurations/types';
import type { Layouts } from '../Viewer/StudyLoader/viewerLoaderState';

/**
 * The only place that writes the viewer's two clinical step atoms.
 *
 * ## Why this module exists
 *
 * A stepped-HP operation has to move several pieces of state together, and the two that matter most
 * are `steppedHPConfigurations` (what the reader sees) and `clinicalStepsBaseline` (what the server
 * last confirmed). The difference between them *is* the dirty indicator, so which of the two a write
 * touches is not an implementation detail -- it is the meaning of the write.
 *
 * Before this module there were 40 production write sites across 7 files, and 5 of those files wrote
 * both atoms and had to keep them consistent by hand. Nothing in the types enforced the pairing, so
 * the failure mode was always the same: a fix handled the paths its ticket travelled through and
 * missed a sibling. That is not a hypothesis. The case-scoped layout override -- the same defect in
 * the same shape one layer down -- was found and fixed four separate times (VX-4624, VX-4732,
 * VX-4768, VX-4772) before `viewerLayoutWriters` gave it an owner, and seven of the SHP tickets in
 * the six weeks to 2026-08-06 were Reset All alone (VX-4740, VX-4772, VX-4783, VX-4789, VX-4811,
 * VX-4815, VX-4862), because Reset All is the operation that has to touch every piece at once.
 *
 * `HangingProtocol/CLAUDE.md` already carries a "step teardown audit" table listing which state each
 * discard-or-restore path must reconcile. The table is correct and inert: it documents an invariant a
 * reviewer is expected to remember. This module is that table made executable -- its columns are the
 * state owned here, its rows are the operations exported below.
 *
 * ## The naming rule
 *
 * Every operation says what it does to the baseline, so a reader can tell whether a call creates,
 * clears, or preserves a dirty indicator without opening the implementation:
 *
 * | Prefix     | Steps    | Baseline            | Dirty indicator |
 * | ---------- | -------- | ------------------- | --------------- |
 * | `apply*`   | moves    | moves with it       | unchanged       |
 * | `stage*`   | moves    | untouched           | appears         |
 * | `commit*`  | untouched| advances to steps   | clears          |
 * | `revert*`  | moves to baseline | untouched  | clears          |
 * | `rollback*`| varies   | returns to pre-save | reappears       |
 *
 * Pick a row. Do not assemble `set` calls -- a direct write to either atom is how the next instance
 * of this bug arrives, and `clinicalStepStore.guard.test.ts` fails the build if one appears.
 *
 * ## The two atoms cross the window boundary together
 *
 * Both are BroadcastChannel-synced, and the pairing is the point: an `apply*` moves the steps and the
 * baseline, so if only the steps crossed, the other window would advance its steps against a
 * standing baseline and light up a dirty dot for an edit nobody made.
 *
 * That is not hypothetical. `clinicalStepsBaseline` was briefly taken off the channel (VX-4862) on
 * the reasoning that a baseline is a *fact* -- derived locally from a server response that both
 * windows receive, so publishing it only creates a race about whose copy wins. The reasoning is
 * sound for a value each window fetches for itself and wrong for a local change that is then
 * published, which is most of what this module does. Measured on the comprehensive live spec: 3
 * phases passing without the effect against 16 with it, the failure being exactly the false dirty
 * dot above.
 *
 * The race VX-4862 named was an *unversioned* republish, and it is defended against by version now
 * rather than by silence -- see `clinicalStepsBaselineMergeFn` in `state.ts`. Highest version wins,
 * on both atoms, so a stale copy is refused instead of overwriting a newer one.
 *
 * ## Calling convention
 *
 * Each function takes Recoil's `set` so it can be called from inside an existing `useRecoilCallback`
 * batch. Most callers are already in one, and splitting these writes across batches makes the grid
 * render a frame stale. This mirrors `viewerLayoutWriters.ts`, whose approach this generalises.
 *
 * Two Recoil traps these functions are written around, both of which have already cost time here:
 * a `set` updater called from inside a `useRecoilCallback` batch runs **lazily**, so the line after
 * a `set` still sees the pre-write value; and `useRecoilCallback`'s `snapshot` can be **stale**
 * relative to an optimistic write that has already committed. Anything that must be fresh is read
 * from `prev` inside an updater.
 *
 * The laziness is conditional on that batch, which matters when testing: a `set` pulled out of
 * `useRecoilCallback` and invoked directly runs its updater **eagerly**. `clinicalStepStore.test.tsx`
 * takes `set` that way, so it cannot observe ordering bugs of this kind -- see the note on
 * {@link dropBaselineForPendingDelete}, where that gap is load-bearing.
 */

// ── Owned deferred-mutation queues ───────────────────────────────────────────────────────────────
//
// Module-level rather than refs so pending operations survive a StepBar unmount/remount -- opening
// the HP edit sidebar remounts it through ResizableStepBar. `useClinicalStepOperations` is also
// mounted in more than one place (StepBar and HangingProtocolWidget), so a ref would give each
// instance its own copy of a queue that must be shared.

/**
 * The handle `setTimeout` returns.
 *
 * Spelled as a union because the DOM lib types it `number` and Node's types it `Timeout`, and which
 * one wins depends on the file's lib resolution -- app code sees `Timeout`, the jsdom test project
 * sees `number`. `clearTimeout` accepts either, so the union costs nothing and keeps the queue
 * accessors usable from a test without a cast.
 */
export type TimerHandle = ReturnType<typeof setTimeout> | number;

/** Deletion timers, keyed by step smid. */
const pendingDeletions = new Map<string, TimerHandle>();

/**
 * What a pending deletion has to remember to be undoable.
 *
 * `step` is the baseline entry as it stood before the delete, so a restore reinstates the exact
 * pre-delete dirty state -- a step with unsaved edits comes back dirty, a saved step comes back
 * clean. `null` means the step had no baseline entry (it was never saved).
 *
 * `index` is where it sat, and it has to be captured here because no restore path can work it out
 * later. Both of them look the step up *after* it has already left the atoms, so a `findIndex`
 * returns -1 and the entry lands at the end of the list instead of where the user deleted it
 * (VX-4815). -1 means the position was not recoverable, and a restore appends.
 */
export interface PendingDeletionBaseline {
  step: StepConfiguration | null;
  index: number;
}

/** Pre-delete baseline entry and position, keyed by step smid. */
const pendingDeletionBaselines = new Map<string, PendingDeletionBaseline>();

export interface PendingSave {
  timer: TimerHandle;
  payload: StepConfigurationInput;
  /**
   * Baseline snapshot captured before the optimistic save advanced it. Used to roll the baseline
   * back if the deferred mutation fails, so the step returns to a dirty state the user can retry.
   */
  rollbackStep: StepConfiguration | null;
}

/** Deferred saves, keyed by step smid. The timer fires the PATCH once the undo window expires. */
const pendingSaves = new Map<string, PendingSave>();

export interface PendingCreate {
  timer: TimerHandle;
  hpSmid: string;
  /** Step index before the optimistic add, so a failed create can restore it (VX-4441). */
  preCreateIndex: number;
}

/**
 * Deferred creates, keyed by tempSmid so concurrent creates (a rapid double-add inside the undo
 * window) are each individually cancellable. A single-slot variable let a second create clobber the
 * first's timer reference, breaking the toast guards and the cleanup effect.
 */
const pendingCreate = new Map<string, PendingCreate>();

/**
 * Temp smids whose Save was requested before their create resolved.
 *
 * A step added optimistically carries the client-generated smid `createDefaultStep` mints, which is
 * not a backend id, so its Save cannot be sent yet. Recording the intent here lets the create flush
 * it once the real smid is in place. Without it the save was dropped outright -- no mutation, no
 * toast, nothing queued to retry -- and the step stayed dirty forever (VX-4810).
 *
 * A plain set rather than a queue: there is nothing to schedule and nothing to cancel, and a second
 * Save inside the same window means the same thing as the first. The step's payload is rebuilt from
 * the atom at flush time, as every other deferred path here does, so nothing needs storing with it.
 */
const savesAwaitingCreate = new Set<string>();

// ── Queue accessors ──────────────────────────────────────────────────────────────────────────────

export const getPendingSave = (smid: string): PendingSave | undefined => pendingSaves.get(smid);
export const setPendingSave = (smid: string, entry: PendingSave): void => {
  pendingSaves.set(smid, entry);
};
export const deletePendingSave = (smid: string): void => {
  pendingSaves.delete(smid);
};
export const pendingSaveSmids = (): string[] => [...pendingSaves.keys()];
export const pendingSaveEntries = (): Array<[string, PendingSave]> => [...pendingSaves.entries()];
export const clearPendingSaves = (): void => {
  pendingSaves.clear();
};

export const getPendingCreate = (smid: string): PendingCreate | undefined =>
  pendingCreate.get(smid);
export const setPendingCreate = (smid: string, entry: PendingCreate): void => {
  pendingCreate.set(smid, entry);
};
export const deletePendingCreate = (smid: string): void => {
  pendingCreate.delete(smid);
};
export const pendingCreateSmids = (): string[] => [...pendingCreate.keys()];
export const pendingCreateEntries = (): Array<[string, PendingCreate]> => [
  ...pendingCreate.entries(),
];
export const clearPendingCreates = (): void => {
  pendingCreate.clear();
};

/** Records that this optimistic step's Save must run once its create confirms a backend smid. */
export const noteSaveAwaitingCreate = (tempSmid: string): void => {
  savesAwaitingCreate.add(tempSmid);
};

/**
 * Removes the record and reports whether there was one, so a create cannot flush the same save twice
 * (both create paths call it, and a retried create would otherwise re-send it).
 */
export const takeSaveAwaitingCreate = (tempSmid: string): boolean =>
  savesAwaitingCreate.delete(tempSmid);

export const getPendingDeletion = (smid: string): TimerHandle | undefined =>
  pendingDeletions.get(smid);
export const setPendingDeletion = (smid: string, timer: TimerHandle): void => {
  pendingDeletions.set(smid, timer);
};
export const deletePendingDeletion = (smid: string): void => {
  pendingDeletions.delete(smid);
  pendingDeletionBaselines.delete(smid);
};
export const pendingDeletionSmids = (): string[] => [...pendingDeletions.keys()];
export const pendingDeletionEntries = (): Array<[string, TimerHandle]> => [
  ...pendingDeletions.entries(),
];
export const clearPendingDeletions = (): void => {
  pendingDeletions.clear();
  pendingDeletionBaselines.clear();
};
export const hasPendingDeletionBaseline = (smid: string): boolean =>
  pendingDeletionBaselines.has(smid);

export const getPendingDeletionBaseline = (smid: string): PendingDeletionBaseline | undefined =>
  pendingDeletionBaselines.get(smid);
export const setPendingDeletionBaseline = (smid: string, entry: PendingDeletionBaseline): void => {
  pendingDeletionBaselines.set(smid, entry);
};

/**
 * Removes and returns a pending deletion's stashed entry, so a restore cannot run twice off the same
 * stash. Returns `null` when nothing was stashed, or when the step had no baseline entry to put back.
 */
export function takePendingDeletionBaseline(smid: string): PendingDeletionBaseline | null {
  const stashed = pendingDeletionBaselines.get(smid);
  pendingDeletionBaselines.delete(smid);
  if (stashed?.step == null) return null;
  return stashed;
}

/**
 * Cancels and forgets every queued mutation.
 *
 * The teardown row for "HP switch / unmount": stale timers from a previous HP must not fire against
 * the new one. Deliberately does not touch either atom -- the caller decides whether the steps
 * themselves are being replaced (an HP switch) or left alone (an unmount).
 */
export function clearAllPendingMutations(): void {
  for (const timer of pendingDeletions.values()) clearTimeout(timer);
  pendingDeletions.clear();
  pendingDeletionBaselines.clear();
  for (const { timer } of pendingSaves.values()) clearTimeout(timer);
  pendingSaves.clear();
  for (const { timer } of pendingCreate.values()) clearTimeout(timer);
  pendingCreate.clear();
  // Nothing to clear a timer for, but a save waiting on a create belongs to the HP being left, and
  // the create it was waiting for has just been cancelled -- so nothing will ever flush it.
  savesAwaitingCreate.clear();
}

/** Test seam: drop every queue without running timers. */
export function resetClinicalStepStoreForTest(): void {
  clearAllPendingMutations();
}

// ── Owned revert snapshots ───────────────────────────────────────────────────────────────────────
//
// The last of the three module-level Maps VX-4810 called out. It lives here for the same reason the
// deferred-mutation queues do: it is a column of the step teardown table, so every path that
// discards or restores a step has to reconcile it, and that is only checkable when one module owns
// the whole row.

export type ClinicalStepSnapshot = {
  viewports: DehydratedViewportsConfigurations | null;
  layouts: Layouts | null;
};

// Module-level snapshot map shared across all useClinicalViewportEditing instances.
// This is necessary because the hook is instantiated in multiple components
// (Viewer/index, StepBar, ViewportDragDropTarget) — per-instance useRef snapshots
// would be invisible across instances, requiring fallback paths.
//
// Keyed by case smid + HP smid + **step smid**. The case and HP are in the key because keying by
// step alone let a different case/HP reuse a stale snapshot, so revert/undo could restore viewports
// from a different protocol entirely (VX-4431).
//
// The step half of the key used to be the array *index*, which is not an identity: a reorder moves
// every step's index while the steps themselves are unchanged, so each pending snapshot stayed
// attached to whatever slid into its old slot and a later revert restored another step's viewports
// (VX-4739). That needed a whole re-keying pass, `remapClinicalSnapshotsForReorder`, called by hand
// from the drop handler -- a patch-up whose absence at any future reorder site would silently
// reintroduce the bug. Keying by smid removes the need for it: a reorder changes no key, so there is
// nothing to fix up and nothing for a new call site to forget.
const clinicalSnapshots = new Map<string, ClinicalStepSnapshot>();

function makeSnapshotKeyPrefix(caseId: Nullable<string>, hpSmid: Nullable<string>): string {
  return `${caseId ?? '__no_case__'}::${hpSmid ?? '__no_hp__'}::`;
}

function makeSnapshotKey(
  caseId: Nullable<string>,
  hpSmid: Nullable<string>,
  stepSmid: Nullable<string>
): string {
  return `${makeSnapshotKeyPrefix(caseId, hpSmid)}${stepSmid ?? '__no_step__'}`;
}

/**
 * Discards all step snapshots without reverting viewer state. Callers should
 * invoke this when switching cases or resetting the HP so stale snapshots from
 * the previous case/HP cannot be used by ensureSnapshot's "already has" guard.
 */
export function clearAllClinicalSnapshots() {
  clinicalSnapshots.clear();
}

/** Reads a step's pending revert snapshot, if one was taken. */
export const getClinicalSnapshot = (
  caseId: Nullable<string>,
  hpSmid: Nullable<string>,
  stepSmid: Nullable<string>
): ClinicalStepSnapshot | undefined =>
  clinicalSnapshots.get(makeSnapshotKey(caseId, hpSmid, stepSmid));

/** Records a step's pre-edit viewports and layouts, so a revert can put them back. */
export const setClinicalSnapshot = (
  caseId: Nullable<string>,
  hpSmid: Nullable<string>,
  stepSmid: Nullable<string>,
  snapshot: ClinicalStepSnapshot
): void => {
  clinicalSnapshots.set(makeSnapshotKey(caseId, hpSmid, stepSmid), snapshot);
};

export const hasClinicalSnapshot = (
  caseId: Nullable<string>,
  hpSmid: Nullable<string>,
  stepSmid: Nullable<string>
): boolean => clinicalSnapshots.has(makeSnapshotKey(caseId, hpSmid, stepSmid));

export const deleteClinicalSnapshot = (
  caseId: Nullable<string>,
  hpSmid: Nullable<string>,
  stepSmid: Nullable<string>
): void => {
  clinicalSnapshots.delete(makeSnapshotKey(caseId, hpSmid, stepSmid));
};

/** The step smids holding a pending snapshot for one case + HP. Drives Reset All's sweep. */
export const clinicalSnapshotStepSmids = (
  caseId: Nullable<string>,
  hpSmid: Nullable<string>
): string[] => {
  const prefix = makeSnapshotKeyPrefix(caseId, hpSmid);
  return [...clinicalSnapshots.keys()]
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length));
};

// ── Internal helpers ─────────────────────────────────────────────────────────────────────────────

// ── Pure step-list updates ───────────────────────────────────────────────────────────────────────
//
// Extracted so the free functions and {@link useClinicalStepWriters} share one definition of what a
// given intent does to the array, while each keeps its own atom plumbing. The hook cannot route
// through the free functions: it has no Recoil `set`, deliberately (see the note on the hook).

/** Replaces the step at `stepIndex`. */
const replaceAt =
  (stepIndex: number, step: StepConfiguration) =>
  (prevSteps: StepConfiguration[] | null): StepConfiguration[] | null =>
    prevSteps?.map((s, i) => (i === stepIndex ? step : s)) ?? null;

/** Maps the step at `stepIndex` through `transform`, leaving the others identical. */
const transformAt =
  (stepIndex: number, transform: (step: StepConfiguration) => StepConfiguration) =>
  (prevSteps: StepConfiguration[] | null): StepConfiguration[] | null =>
    prevSteps?.map((s, i) => (i === stepIndex ? transform(s) : s)) ?? null;

/** Resolves a `StepsUpdater` against the previous list. */
const resolveUpdater =
  (update: StepsUpdater) =>
  (prevSteps: StepConfiguration[] | null): StepConfiguration[] | null =>
    typeof update === 'function' ? update(prevSteps) : update;

/**
 * Every steps write goes through here, so the version counter can never be skipped.
 *
 * `intent` names the operation for {@link traceHPMutation}. It is required rather than optional
 * because the whole value of the trace is that a write can be attributed without a stack -- and
 * Recoil's batching means there is no stack to fall back on. See `hpMutationTrace.ts`.
 */
function writeSteps(
  set: CallbackInterface['set'],
  intent: string,
  update: (prevSteps: StepConfiguration[] | null) => StepConfiguration[] | null,
  name?: string | null
): void {
  set(steppedHPConfigurations, (prev: VersionedStepConfigurations) => {
    const next = makeVersionedSteps(update(prev.steps), prev, name);
    traceHPMutation(intent, {
      atom: 'steps',
      previousVersion: prev.version,
      nextVersion: next.version,
      steps: next.steps?.length ?? null,
      changed: next.steps !== prev.steps,
    });
    return next;
  });
}

/** Every baseline write goes through here, so a `structuredClone`-vs-alias slip has one home. */
function writeBaseline(
  set: CallbackInterface['set'],
  intent: string,
  update: (prev: StepConfiguration[] | null) => StepConfiguration[] | null
): void {
  set(clinicalStepsBaseline, (prev) => {
    const next = update(prev);
    traceHPMutation(intent, {
      atom: 'baseline',
      steps: next?.length ?? null,
      changed: next !== prev,
    });
    return next;
  });
}

/**
 * The server's baseline, except for steps whose own write is still in flight.
 *
 * A clinical save advances the baseline optimistically and defers the PATCH behind the undo window,
 * so for a second or two the server still holds the pre-save step. Any HP fetch resolving in that
 * gap returns a protocol that is correct about every step except the one being saved -- and the
 * fetch is at its *most* likely right then, because `executeSaveOnBackend` drops the precache when
 * the mutation lands.
 *
 * Writing that response over the baseline wholesale walks the just-saved entry backwards. Because
 * the baseline is broadcast, it walks backwards in the *other* window too: measured on
 * `stepped-hp-priors-per-step`, viewer/0 committed `2,3` at v6, viewer/1's refetch published the
 * pre-PATCH `2,2` as v7, and v0 accepted it on version order. The next revert then wrote that stale
 * value into the step, silently undoing the save.
 *
 * Two fixes were tried and both were wrong in the same way -- they treated this as a question about
 * the *whole* baseline. Withholding the server baseline from the broadcast reintroduced VX-4862's
 * false dirty dot (29 passing phases to 3); holding the whole baseline back during a create costs
 * half the suite, which is why `applyServerBaselineOnly` exists at all. The question is per step:
 * the server can speak for every entry except the ones with an outstanding write, so only those are
 * held. `isPendingStepWrite` reads the broadcast registry, so a save deferred in either window
 * counts -- and it is the *other* window's fetch that does the damage.
 *
 * Held entries are released by `endPendingStepSave` in `executeSaveOnBackend`'s `finally`, so a
 * PATCH that lands, fails, or is undone all converge on the server's copy. A window that dies
 * mid-save is covered by the registry's own TTL rather than by anything here.
 */
function baselineFromServer(
  serverSteps: Nullable<StepConfiguration[]>,
  prevBaseline: StepConfiguration[] | null
): StepConfiguration[] | null {
  if (!serverSteps) return null;
  const held = new Map(
    (prevBaseline ?? [])
      .filter((step): step is StepConfiguration & { smid: string } => step.smid != null)
      .filter((step) => isPendingStepWrite(step.smid))
      .map((step) => [step.smid, step])
  );
  if (held.size === 0) return structuredClone(serverSteps);
  return serverSteps.map((step) => {
    const heldStep = step.smid != null ? held.get(step.smid) : undefined;
    return heldStep ? clone(heldStep) : structuredClone(step);
  });
}

// ── apply*: steps and baseline move together, dirty state unchanged ──────────────────────────────

/**
 * Adopts a freshly fetched protocol: the steps the caller resolved, and the server's own copy as the
 * new baseline.
 *
 * `resolveSteps` receives the *previous* steps rather than a snapshot value because an optimistic
 * write can commit between this call and the updater running, and a snapshot would still report the
 * pre-write list. The merge that decides whether an unsaved local edit survives the re-read belongs
 * to the caller (`useHangingProtocol`), which is the only place that knows whether this response is a
 * re-read of the same HP or a genuine protocol change.
 *
 * The baseline is *always* the server's copy. A baseline of `null` marks every step dirty and a
 * baseline equal to the current steps marks none; both are the failure VX-4624 described.
 */
export function applyServerSteps(
  set: CallbackInterface['set'],
  {
    serverSteps,
    resolveSteps,
    name,
  }: {
    serverSteps: Nullable<StepConfiguration[]>;
    // Nullable rather than `| null` because `mergeUnsavedStepEdits` can return undefined; both mean
    // "this HP has no steps" and are normalised to null here rather than at each call site.
    resolveSteps: (prevSteps: StepConfiguration[] | null) => Nullable<StepConfiguration[]>;
    name?: string | null;
  }
): void {
  writeSteps(set, 'applyServerSteps', (prevSteps) => resolveSteps(prevSteps) ?? null, name);
  writeBaseline(set, 'applyServerSteps', (prev) => baselineFromServer(serverSteps, prev));
}

/**
 * Records what the server holds without touching the steps.
 *
 * The one deliberate asymmetry in this module, and it is measured rather than assumed. While a step
 * create is in flight the steps atom is ahead of the server by exactly the optimistic step, so the
 * re-init holds the steps back -- but the tempting symmetry of holding the baseline back too drops
 * the comprehensive live spec from 20 passing phases to 10. A deferred save that flushes during a
 * create needs the baseline to match what the server now holds, or the step it just saved reads as
 * unsaved again.
 */
export function applyServerBaselineOnly(
  set: CallbackInterface['set'],
  { serverSteps }: { serverSteps: Nullable<StepConfiguration[]> }
): void {
  writeBaseline(set, 'applyServerBaselineOnly', (prev) => baselineFromServer(serverSteps, prev));
}

/**
 * Publishes the steps an HP form save just persisted, and baselines the same value.
 *
 * Baseline the *merged* steps the viewer is about to render, never the flushed form steps: the form
 * works in the stored (criteria-only) shape, so its steps carry null study/series smids, and
 * baselining those makes the re-attached resolved viewports read as dirty (VX-4764).
 */
export function applySavedSteps(
  set: CallbackInterface['set'],
  { steps, name }: { steps: StepConfiguration[] | null; name?: string | null }
): void {
  writeSteps(set, 'applySavedSteps', () => steps, name);
  writeBaseline(set, 'applySavedSteps', () => (steps ? structuredClone(steps) : null));
}

/**
 * Publishes a new HP display name without touching the steps or the baseline.
 *
 * The name rides on the steps atom so `activeHangingProtocolName` can be a pure selector, which means
 * a rename has to go through a steps write even when no step changed. Separate from
 * {@link applySavedSteps} so the non-stepped save path cannot accidentally republish steps.
 *
 * Idempotent by design: an unchanged name writes nothing. Bumping the version for a no-op would
 * broadcast a fresh value to the other window on every render that reaches here, and the init effect
 * reaches here on every render where the guard blocks a full re-init.
 */
export function applyProtocolName(
  set: CallbackInterface['set'],
  { name }: { name: string | null }
): void {
  set(steppedHPConfigurations, (prev: VersionedStepConfigurations) =>
    prev.name === name ? prev : makeVersionedSteps(prev.steps, prev, name)
  );
}

/**
 * Records server-derived series criteria on one viewport, in whichever of the two atoms still holds
 * the series the classification was requested for.
 *
 * Criteria are derived from the series by the server; the user did not type them, so their arrival
 * must never be what makes a step dirty -- which is why this is an `apply` and why both atoms are
 * addressed in one operation. Each write is guarded separately, and both guards were paid for:
 *
 * - **Steps.** These fetches outlive the drag that started them. If the step is reverted while one is
 *   in flight, writing anyway stamps the old series' criteria onto the restored viewport; every other
 *   field came back from the baseline, so the step differed by `criteria` alone and read dirty
 *   forever -- reverting again could not clear it, because reverting is what triggered it.
 * - **Baseline.** Saving a step while its classification is in flight baselines it without criteria;
 *   the response then lands and mutates the step, leaving it different from the baseline it was just
 *   saved against. But if the user has dragged a *new* series and not saved, the baseline still
 *   describes the old one, and writing there would fabricate a baseline that never existed -- old
 *   series, new criteria -- hiding a genuine unsaved change. So the baseline only takes the criteria
 *   when it independently agrees about which series the viewport holds.
 *
 * The identity check is `series_smid`, not the series description text: a description can be empty
 * or absent for a poorly-labeled series, and it is derived the same way a display name is, so a
 * future change to display-name derivation could silently stop matches. The smid is what the drag
 * assigned and is not derived from anything display-related.
 */
type CriteriaArgs = {
  stepIndex: number;
  viewportId: string;
  criteria: Array<{ category: string; value: string }>;
  seriesSmid: Nullable<string>;
};

/**
 * The two guarded updates {@link applyServerCriteriaToViewport} applies, as pure functions.
 *
 * Shared with {@link useClinicalStepWriters} so both paths cannot drift on the guards, which are the
 * whole substance of the operation.
 */
function criteriaUpdatesFor({ stepIndex, viewportId, criteria, seriesSmid }: CriteriaArgs): {
  stepsUpdate: (prev: StepConfiguration[] | null) => StepConfiguration[] | null;
  baselineUpdate: (prev: StepConfiguration[] | null) => StepConfiguration[] | null;
} {
  const holdsSeries = (step: StepConfiguration | undefined): boolean => {
    const viewport = step?.viewports?.[viewportId];
    if (viewport == null || seriesSmid == null) return false;
    return viewport.series_smid === seriesSmid;
  };
  const withCriteria = (step: StepConfiguration): StepConfiguration =>
    assocPath(['viewports', viewportId, 'criteria'], criteria, step);

  return {
    stepsUpdate: (prev) => {
      if (prev == null || !holdsSeries(prev[stepIndex])) return prev;
      return prev.map((step, i) => (i === stepIndex ? withCriteria(step) : step));
    },
    baselineUpdate: (prev) => {
      if (prev == null || !holdsSeries(prev[stepIndex])) return prev;
      return prev.map((step, i) => (i === stepIndex ? withCriteria(step) : step));
    },
  };
}

export function applyServerCriteriaToViewport(
  set: CallbackInterface['set'],
  args: CriteriaArgs
): void {
  const { stepsUpdate, baselineUpdate } = criteriaUpdatesFor(args);
  writeSteps(set, 'applyServerCriteriaToViewport', stepsUpdate);
  writeBaseline(set, 'applyServerCriteriaToViewport', baselineUpdate);
}

/**
 * Empties both step atoms. The teardown row for "HP deleted".
 *
 * Deliberately does not touch the queues. Deleting an HP changes `activeHangingProtocolSmidState`,
 * and `useClinicalStepOperations`' effect on that key already runs {@link clearAllPendingMutations}.
 * Folding it in here would be redundant today and would change the order in which a pending save is
 * abandoned relative to the atoms being cleared, which is not what this refactor is for.
 */
export function clearAllSteps(set: CallbackInterface['set']): void {
  writeSteps(set, 'clearAllSteps', () => null, null);
  writeBaseline(set, 'clearAllSteps', () => null);
  // Folded in from `useClearSteppedHPState`, which called it immediately after this in the same
  // callback. Behaviour is identical; what changes is that the HP-deleted teardown row is now one
  // operation rather than two things a caller has to remember to pair.
  clearAllClinicalSnapshots();
}

// ── stage*: steps move, baseline untouched -- this is what makes a step read dirty ───────────────

/** Replaces one step by index, leaving the baseline alone so the edit shows as unsaved. */
export function stageStepEdit(
  set: CallbackInterface['set'],
  {
    stepIndex,
    step,
  }: {
    stepIndex: number;
    step: StepConfiguration;
  }
): void {
  writeSteps(set, 'stageStepEdit', replaceAt(stepIndex, step));
}

/** Transforms one step in place, leaving the baseline alone. For edits derived from the old value. */
export function stageStepTransform(
  set: CallbackInterface['set'],
  {
    stepIndex,
    transform,
  }: {
    stepIndex: number;
    transform: (step: StepConfiguration) => StepConfiguration;
  }
): void {
  writeSteps(set, 'stageStepTransform', transformAt(stepIndex, transform));
}

/**
 * Applies an arbitrary steps-list intent -- rename, reorder, drag, delete -- without touching the
 * baseline.
 *
 * The escape hatch for whole-list operations. Prefer a named operation when one fits; this exists
 * because `useHPStepOperations` drives generic CRUD over an injected setter.
 */
export function stageSteps(
  set: CallbackInterface['set'],
  { update }: { update: StepsUpdater }
): void {
  writeSteps(set, 'stageSteps', resolveUpdater(update));
}

/** Replaces several steps at once by index, leaving the baseline alone. */
export function stageStepEdits(
  set: CallbackInterface['set'],
  { stepsByIndex }: { stepsByIndex: Map<number, StepConfiguration> }
): void {
  if (stepsByIndex.size === 0) return;
  stageSteps(set, {
    update: (prevSteps) => prevSteps?.map((s, i) => stepsByIndex.get(i) ?? s) ?? null,
  });
}

// ── commit*: baseline advances to match the steps, clearing the dirty indicator ──────────────────

/**
 * Advances one step's baseline to the content just sent to the backend.
 *
 * Optimistic: the indicator clears before the mutation resolves. The caller must capture the
 * pre-save entry first and hand it to {@link rollbackStepBaseline} if the deferred write fails.
 */
export function commitStepBaseline(
  set: CallbackInterface['set'],
  { stepIndex, step }: { stepIndex: number; step: StepConfiguration }
): void {
  writeBaseline(set, 'commitStepBaseline', (prev) => {
    if (!prev) return prev;
    const next = [...prev];
    next[stepIndex] = clone(step);
    return next;
  });
}

/** Returns one step's baseline to its pre-save value, so a failed save reads dirty and retryable. */
export function rollbackStepBaseline(
  set: CallbackInterface['set'],
  { smid, rollbackStep }: { smid: string; rollbackStep: StepConfiguration }
): void {
  writeBaseline(set, 'rollbackStepBaseline', (prev) =>
    prev ? prev.map((s) => (s.smid === smid ? rollbackStep : s)) : prev
  );
}

/**
 * The by-index form of {@link rollbackStepBaseline}, for the paths that already hold the index.
 *
 * Without this the optimistic advance in {@link commitStepBaseline} leaves baseline equal to the
 * step, `isStepEqual` stays true, and the dirty indicator never reappears after a failed save.
 */
export function rollbackStepBaselineAt(
  set: CallbackInterface['set'],
  { stepIndex, rollbackStep }: { stepIndex: number; rollbackStep: StepConfiguration }
): void {
  writeBaseline(set, 'rollbackStepBaselineAt', (prev) => {
    if (!prev) return prev;
    const next = [...prev];
    next[stepIndex] = rollbackStep;
    return next;
  });
}

/**
 * Rewrites the baseline in the steps' current order, without changing any entry's content.
 *
 * A reorder moves steps but does not edit them, so entries are matched by smid and only moved -- a
 * dirty step stays dirty and a clean step stays clean. The array is kept index-aligned with the
 * steps because the revert paths index into it.
 */
export function applyBaselineOrder(
  set: CallbackInterface['set'],
  { baseline }: { baseline: StepConfiguration[] }
): void {
  writeBaseline(set, 'applyBaselineOrder', () => baseline);
}

// ── revert*: steps move back to the baseline, clearing the dirty indicator ───────────────────────

/** Restores the given step indices from a baseline the caller has already read. */
export function revertStepsToBaseline(
  set: CallbackInterface['set'],
  { indices, baseline }: { indices: ReadonlySet<number>; baseline: StepConfiguration[] }
): void {
  if (indices.size === 0) return;
  writeSteps(
    set,
    'revertStepsToBaseline',
    (prevSteps) =>
      prevSteps?.map((step, i) => (indices.has(i) && baseline[i] ? clone(baseline[i]) : step)) ??
      null
  );
}

/**
 * Replaces the whole steps list with a baseline the caller has already filtered. The Reset All row.
 *
 * Takes the baseline as an argument rather than reading it, because Reset All must first remove
 * anything unsaved *from the baseline* -- a `set` does not retroactively change a value the caller
 * already read, and restoring from an unfiltered baseline is what let VX-4811's unsaved step survive
 * the reset.
 */
export function revertAllToBaseline(
  set: CallbackInterface['set'],
  { baseline }: { baseline: StepConfiguration[] }
): void {
  writeSteps(set, 'revertAllToBaseline', () => clone(baseline));
}

/** Removes entries from the baseline. Used by Reset All to drop steps that were never saved. */
export function pruneBaseline(
  set: CallbackInterface['set'],
  { keep }: { keep: (step: StepConfiguration) => boolean }
): void {
  writeBaseline(set, 'pruneBaseline', (prev) => prev?.filter(keep) ?? null);
}

// ── Optimistic create: both atoms gain the step, so it does not read dirty ───────────────────────

/**
 * Adds a step to both atoms so it appears immediately without a dirty indicator.
 *
 * It carries a client-generated `step-{uuid}` smid until the backend answers. That smid is what marks
 * it unsaved, and every discard path keys off it rather than off whichever queue happens to know
 * about it -- there are two create paths and only one of them is deferred (VX-4811).
 */
export function addOptimisticStep(
  set: CallbackInterface['set'],
  { step }: { step: StepConfiguration }
): void {
  writeSteps(set, 'addOptimisticStep', (prevSteps) => (prevSteps ? [...prevSteps, step] : [step]));
  writeBaseline(set, 'addOptimisticStep', (prev) =>
    prev ? [...prev, clone(step)] : [clone(step)]
  );
}

/**
 * Swaps a temporary smid for the one the backend issued, in both atoms.
 *
 * `persistedStep`, when given, replaces the baseline entry with the content actually sent rather than
 * just re-keying it -- the deferred path rebuilds its payload at fire time, so the baseline has to
 * record what went over the wire or the step reads dirty the moment it lands.
 *
 * The **attributed step index** is re-keyed too, and it has to be: the reader is normally sitting on
 * the step that is being confirmed, so leaving its `stepSmid` on the temp id points the selection at
 * an id no list contains from this moment on. Identity resolution then fails for good and every later
 * reorder, delete or cross-window refetch falls back to the raw number -- the exact failure the
 * attribution exists to prevent, arriving a few hundred milliseconds after the create looked fine.
 */
export function confirmOptimisticStep(
  set: CallbackInterface['set'],
  {
    tempSmid,
    serverSmid,
    persistedStep,
  }: { tempSmid: string; serverSmid: string; persistedStep?: StepConfiguration }
): void {
  writeSteps(
    set,
    'confirmOptimisticStep',
    (prevSteps) =>
      prevSteps?.map((s) => (s.smid === tempSmid ? { ...s, smid: serverSmid } : s)) ?? null
  );
  writeBaseline(set, 'confirmOptimisticStep', (prev) => {
    if (persistedStep == null) {
      return prev?.map((s) => (s.smid === tempSmid ? { ...s, smid: serverSmid } : s)) ?? null;
    }
    const synced = { ...persistedStep, smid: serverSmid };
    if (!prev) return [clone(synced)];
    return prev.some((s) => s.smid === tempSmid)
      ? prev.map((s) => (s.smid === tempSmid ? clone(synced) : s))
      : [...prev, clone(synced)];
  });
  set(hangingProtocolStepIndexAttributed, (prev) =>
    prev.stepSmid === tempSmid ? { ...prev, stepSmid: serverSmid } : prev
  );
}

/** Removes a failed optimistic create from both atoms. */
export function rollbackOptimisticStep(
  set: CallbackInterface['set'],
  { tempSmid }: { tempSmid: string }
): void {
  writeSteps(
    set,
    'rollbackOptimisticStep',
    (prevSteps) => prevSteps?.filter((s) => s.smid !== tempSmid) ?? null
  );
  writeBaseline(
    set,
    'rollbackOptimisticStep',
    (prev) => prev?.filter((s) => s.smid !== tempSmid) ?? null
  );
}

/**
 * Undoes optimistic creates completely: cancels the deferred backend write and removes each step from
 * both atoms that were given a copy of it.
 *
 * An optimistic create leaves the new step in three places -- the steps atom, the baseline, and a
 * `pendingCreate` timer -- and anything that discards it has to undo all three. The toast's UNDO did;
 * Reset All did none of them, so the step survived the reset (the baseline it restores from held a
 * copy) and the create landed seconds later on top of the refetched server state, leaving two chips
 * with one name (VX-4811).
 *
 * Returns the temp smids it discarded, so a caller already holding a baseline snapshot can filter
 * them out of the copy in its hand -- a `set` here does not retroactively change a value already read.
 */
export function discardPendingCreates(
  set: CallbackInterface['set'],
  tempSmids: ReadonlyArray<string>,
  onDiscarded: (count: number) => void
): ReadonlySet<string> {
  const discarded = new Set<string>();
  for (const tempSmid of tempSmids) {
    const pending = pendingCreate.get(tempSmid);
    if (pending == null) continue;
    clearTimeout(pending.timer);
    pendingCreate.delete(tempSmid);
    endPendingStepCreate(tempSmid);
    discarded.add(tempSmid);
  }
  if (discarded.size === 0) return discarded;

  writeSteps(
    set,
    'discardPendingCreates',
    (prevSteps) => prevSteps?.filter((s) => !discarded.has(s.smid ?? '')) ?? null
  );
  writeBaseline(
    set,
    'discardPendingCreates',
    (prev) => prev?.filter((s) => !discarded.has(s.smid ?? '')) ?? null
  );
  onDiscarded(discarded.size);
  return discarded;
}

// ── Delete ───────────────────────────────────────────────────────────────────────────────────────

/**
 * Drops a step's baseline entry, stashing it so an undo can restore the exact pre-delete dirty state.
 *
 * Called when the deletion is *scheduled*, not when it lands, so the remaining steps do not sprout
 * dirty indicators while the undo window runs (VX-4625).
 */
export function dropBaselineForPendingDelete(
  set: CallbackInterface['set'],
  { smid }: { smid: string }
): void {
  // HACK: the stash is a side effect inside the updater, which is unusual and would normally be
  // worth sequencing out. It cannot be here. Called from inside a `useRecoilCallback` body -- this
  // module's calling convention, and what `scheduleDeletion` does -- Recoil runs the `set` updater
  // *lazily*, so the line after `writeBaseline` returns executes before this callback does.
  // Measured ordering: `before set`, `after set returns`, `inside updater`. Capturing `prev` into a
  // variable and reading it after `writeBaseline` returns -- the sequential-looking alternative --
  // therefore reads that variable while it is still unassigned, and stashes null. `prev` is also
  // the only value guaranteed fresh with respect to an optimistic write that committed since this
  // callback was invoked, so reading it any earlier is wrong on top of being impossible.
  //
  // Note the laziness is *not* universal: a `set` pulled out of `useRecoilCallback` and invoked
  // directly, which is what `clinicalStepStore.test.tsx` does, runs its updater eagerly. So the
  // store-level tests here pass against the broken sequential form; only the hook-level tests in
  // `useClinicalStepOperations.test.tsx` catch it (verified -- the rewrite fails exactly two of
  // them, both in the `scheduleDeletion` describe). Re-check those, not just this file's tests, if
  // you ever revisit this.
  writeBaseline(set, 'dropBaselineForPendingDelete', (prev) => {
    // The position is captured here for the same reason the entry is: this updater holds the only
    // view of the list that still contains the step. Every restore path runs after it is gone.
    setPendingDeletionBaseline(smid, {
      step: prev?.find((s) => s.smid === smid) ?? null,
      index: prev?.findIndex((s) => s.smid === smid) ?? -1,
    });
    return prev?.filter((s) => s.smid !== smid) ?? null;
  });
}

/** Drops a step's baseline entry once the backend has confirmed the deletion. */
export function dropBaselineAfterDelete(
  set: CallbackInterface['set'],
  { smid }: { smid: string }
): void {
  writeBaseline(
    set,
    'dropBaselineAfterDelete',
    (prev) => prev?.filter((s) => s.smid !== smid) ?? null
  );
}

/**
 * Puts a step back into a list at the position it was deleted from.
 *
 * The one place reinsertion order is decided, so the two paths that undo a deletion -- the toast's
 * UNDO and Reset All -- cannot disagree about it. They did before: UNDO restored the baseline entry
 * and Reset All committed the delete outright, and the shared helper is what stops that reopening
 * (VX-4815). Already-present is a no-op rather than a duplicate, because UNDO's caller restores the
 * steps atom from its own full snapshot before this runs.
 */
export function reinsertStepAt(
  list: Nullable<StepConfiguration[]>,
  { step, index }: PendingDeletionBaseline
): StepConfiguration[] | null {
  if (step == null) return list ?? null;
  if (!list) return [clone(step)];
  if (list.some((s) => s.smid === step.smid)) return list;
  const next = [...list];
  next.splice(index >= 0 && index <= next.length ? index : next.length, 0, clone(step));
  return next;
}

/**
 * Puts a step's stashed baseline entry back when a pending deletion is undone.
 *
 * The position comes from the stash rather than from a lookup, because by the time any caller gets
 * here the step is gone from the atoms and a lookup returns -1 (VX-4815).
 */
export function restoreBaselineForUndoneDelete(
  set: CallbackInterface['set'],
  { smid }: { smid: string }
): void {
  const stashed = takePendingDeletionBaseline(smid);
  if (stashed == null) return;
  writeBaseline(set, 'restoreBaselineForUndoneDelete', (prev) => reinsertStepAt(prev, stashed));
}

/**
 * Cancels every pending deletion without firing it, and hands back what each one needs to be put
 * back. The Reset All row of the teardown table: the modal promises a revert to the last saved
 * version, and a step whose delete is still inside its undo window has not reached that version yet
 * (VX-4815).
 *
 * Returns the entries rather than writing the atoms, because Reset All rebuilds the whole step list
 * from a baseline array it is already holding -- a `set` here would not be visible in that array.
 *
 * **In deletion order**, which the caller relies on: each entry's `index` was captured against the
 * list as it stood just before that step left it, so the entries are only meaningful replayed in
 * reverse. `pendingDeletions` is a Map, so iteration is insertion order, and an undone-then-redeleted
 * step re-enters at the end because the undo path deletes its key first.
 */
export function cancelAllPendingDeletions(): Array<PendingDeletionBaseline & { smid: string }> {
  const cancelled: Array<PendingDeletionBaseline & { smid: string }> = [];
  for (const [smid, timer] of pendingDeletions.entries()) {
    clearTimeout(timer);
    const stashed = takePendingDeletionBaseline(smid);
    if (stashed != null) cancelled.push({ smid, ...stashed });
  }
  pendingDeletions.clear();
  pendingDeletionBaselines.clear();
  return cancelled;
}

// ── Step and sub-step index: where in the protocol the reader is ─────────────────────────────────

/**
 * Moves the active step.
 *
 * Owned here for the reason the steps themselves are. A step-index write is a publish decision with
 * exactly the same inputs as a steps write -- who is writing, what is in flight, whether the target
 * already holds user intent -- and `hangingProtocolStepIndex` is the one piece of step state that is
 * broadcast, unscoped, *and* written by an effect that reasons about protocol identity. Splitting
 * the two is what let a guard carefully reasoned about for `.steps` reset the index with no guard at
 * all: viewer/1's deferred re-init homed to 0 roughly eight seconds after the user selected step 1
 * in viewer/0, and because the atom is BroadcastChannel-synced, viewer/0 followed it
 * (`stepped-hp-edit-form-reopen:64`).
 *
 * Deliberately leaves the sub-step alone. Most callers that move the step also want the sub-step
 * back at 0, but not all of them -- the create-undo paths put the reader back exactly where they
 * were -- so a caller that wants the reset states it, with {@link selectSubStep} or
 * {@link resetStepIndices}, rather than having it happen here by implication.
 *
 * Leaves the index's `ownerHPKey` attribution alone, for the same reason the hook form does: adding a
 * step, undoing a create and moving to a neighbour are all navigation *within* the protocol the reader
 * is already in. Which protocol the index belongs to changes only in {@link resetStepIndices}.
 */
export function selectStep(set: CallbackInterface['set'], { index }: { index: number }): void {
  set(hangingProtocolStepIndex, index);
}

/**
 * Moves the active step and states the identity, instead of letting the selector resolve one.
 *
 * {@link selectStep} derives `stepSmid` by looking `index` up in `steppedHPConfigurations`, which is
 * correct only when the caller is navigating that list and it already holds the target step. Two
 * callers satisfy neither condition, and both were silently mis-stamped before this existed:
 *
 * - **An optimistic create.** `addOptimisticStep` and the following `selectStep` run in one
 *   `useRecoilCallback` batch, where the steps updater is applied *lazily* -- so the lookup reads the
 *   pre-append list, finds nothing at the new step's index, and stores `stepSmid: null`. The reader
 *   then falls back to the number until their next navigation re-stamps it, which is exactly the
 *   window a cross-window reorder moves them off the step they just added.
 * - **The HP edit form**, which navigates `hpFormStepConfigurations`. That list can diverge from the
 *   viewer's, so slot N there is not slot N here; resolving against the viewer list records some
 *   other step entirely. Form callers pass `stepSmid: null` to say "position only, no identity",
 *   which is what the form has always meant and leaves the read on the stored index.
 *
 * `null` is therefore a real argument rather than an absent one, and the parameter is required so a
 * caller has to decide which of the two they are.
 *
 * Leaves `ownerHPKey` alone, like {@link selectStep}: this is still navigation within a protocol.
 */
export function selectStepWithIdentity(
  set: CallbackInterface['set'],
  { index, stepSmid }: { index: number; stepSmid: Nullable<string> }
): void {
  set(hangingProtocolStepIndexAttributed, (prev) => ({
    ...prev,
    index,
    stepSmid: stepSmid ?? null,
  }));
}

/** Moves the sub-step within the active step. Prior scrolling; see `calculateSubStepTotal`. */
export function selectSubStep(set: CallbackInterface['set'], { index }: { index: number }): void {
  set(hangingProtocolSubStepIndex, index);
}

/**
 * Both indices back to the start of the protocol.
 *
 * The teardown-table row shared by Reset All and HP switch/unmount: whatever the reader was looking
 * at belonged to a protocol state that no longer exists, so leaving either index where it stood
 * points it at a step that may not be there.
 *
 * `forHPKey` is nullable here, unlike {@link selectStep}: an HP switch or unmount resets the indices
 * precisely because there is no longer a protocol they belong to, and claiming ownership for the
 * departing HP would tell the next init pass that someone had positioned the index for it. Reset All
 * *does* pass a key -- it stays on the same protocol, and step 0 is where it means the reader to be.
 */
export function resetStepIndices(
  set: CallbackInterface['set'],
  { forHPKey }: { forHPKey: Nullable<string> }
): void {
  // `stepSmid: null` rather than the arriving protocol's step 0, and the asymmetry with
  // {@link selectStep} is the point. Homing means "put the reader at the start of whatever this
  // protocol turns out to be", and the steps it refers to are frequently published in the same batch
  // as this write -- so a smid sampled here would name the *departing* protocol's step 0. Leaving it
  // null makes the stored index authoritative until the reader's first real navigation stamps one.
  set(hangingProtocolStepIndexAttributed, {
    index: 0,
    stepSmid: null,
    ownerHPKey: forHPKey ?? null,
  });
  set(hangingProtocolSubStepIndex, 0);
}

// ── Component-level access ───────────────────────────────────────────────────────────────────────

/**
 * The operations a component needs without a surrounding `useRecoilCallback`, built on plain Recoil
 * setters.
 *
 * **Plain setters, not `useRecoilCallback`, and that is load-bearing.** Several of these are called
 * from *inside* another Recoil callback -- `ViewportDragDropTarget`'s `handleDropViewport` and
 * `handleDropThumbnail`, and the `ClearViewport` mouse adaptor. Nesting one `useRecoilCallback`
 * invocation inside another changes when the inner write commits relative to the outer transaction,
 * and every one of these call sites used a bare `useSetRecoilState` before this module existed.
 * Wrapping them in callbacks here was a silent mechanism change and broke VX-4733's live
 * reproduction (clear a viewport, then drop a series into it). Keep them plain.
 *
 * The setters are created *here*, inside the owning module, so `clinicalStepStore.guard.test.ts`
 * still sees exactly one writer of each atom.
 */
export const useClinicalStepWriters = (): {
  applyServerCriteriaToViewport: (
    args: Parameters<typeof applyServerCriteriaToViewport>[1]
  ) => void;
  applySavedSteps: (args: Parameters<typeof applySavedSteps>[1]) => void;
  applyProtocolName: (args: Parameters<typeof applyProtocolName>[1]) => void;
  /** Curried for the CRUD hooks, which pass a bare `StepsUpdater` through an injected setter. */
  stageViewerSteps: (update: StepsUpdater) => void;
  /** Curried step-scoped edit for the viewport editors, which edit one step at a time. */
  stageStepAt: (
    stepIndex: number,
    transform: (step: StepConfiguration) => StepConfiguration
  ) => void;
  /**
   * {@link selectStep} for the navigation hooks, which move the index outside a Recoil callback.
   *
   * **Moves the reader within the protocol they are already in, so it deliberately does not touch the
   * attribution** -- it writes through the `hangingProtocolStepIndex` selector, which carries
   * `ownerHPKey` through unchanged. A chip click, a keyboard shortcut and a form step switch are all
   * navigation *inside* a protocol; none of them changes which protocol the index belongs to.
   *
   * This is why no navigation hook needs to know the hpKey, and why this hook takes no dependency on
   * the router or the current-case context. Ownership changes only when the protocol does, which is
   * {@link resetStepIndices}' job.
   */
  selectStep: (index: number) => void;
  /**
   * {@link selectStepWithIdentity} for the navigation hooks -- for the callers that cannot let the
   * selector resolve the identity, i.e. the HP edit form (its list is not the viewer's) and an
   * optimistic create (the step is not in the viewer's list yet). See that function for why.
   */
  selectStepWithIdentity: (args: { index: number; stepSmid: Nullable<string> }) => void;
  /** {@link selectSubStep}, same. */
  selectSubStep: (index: number) => void;
  /**
   * {@link resetStepIndices} for `useHangingProtocol`, which homes outside a Recoil callback.
   *
   * Requires the key because this is the operation that *changes* which protocol the index belongs
   * to: homing is what happens when a protocol the reader has not navigated arrives, and recording it
   * is what stops the next re-read homing again.
   */
  resetStepIndices: (args: { forHPKey: Nullable<string> }) => void;
} => {
  const setVersioned = useSetRecoilState(steppedHPConfigurations);
  const setBaselineAtom = useSetRecoilState(clinicalStepsBaseline);
  const setAttributedStepIndex = useSetRecoilState(hangingProtocolStepIndexAttributed);
  const setStepIndexPreservingOwner = useSetRecoilState(hangingProtocolStepIndex);
  const setSubStepIndexAtom = useSetRecoilState(hangingProtocolSubStepIndex);

  const selectStepFn = useCallback(
    (index: number) => setStepIndexPreservingOwner(index),
    [setStepIndexPreservingOwner]
  );
  const selectStepWithIdentityFn = useCallback(
    ({ index, stepSmid }: { index: number; stepSmid: Nullable<string> }) => {
      // Mirrors the free-function form above -- see its comment for why the identity is stated here
      // rather than resolved from `steppedHPConfigurations`.
      setAttributedStepIndex((prev) => ({ ...prev, index, stepSmid: stepSmid ?? null }));
    },
    [setAttributedStepIndex]
  );
  const selectSubStepFn = useCallback(
    (index: number) => setSubStepIndexAtom(index),
    [setSubStepIndexAtom]
  );
  const resetStepIndicesFn = useCallback(
    ({ forHPKey }: { forHPKey: Nullable<string> }) => {
      // Mirrors the free-function form above, `stepSmid: null` included -- see its comment.
      setAttributedStepIndex({ index: 0, stepSmid: null, ownerHPKey: forHPKey ?? null });
      setSubStepIndexAtom(0);
    },
    [setAttributedStepIndex, setSubStepIndexAtom]
  );

  /** Applies a pure step-list update through the versioned wrapper. */
  const writeStepsVia = useCallback(
    (
      update: (prevSteps: StepConfiguration[] | null) => StepConfiguration[] | null,
      name?: string | null
    ) => {
      setVersioned((prev) => makeVersionedSteps(update(prev.steps), prev, name));
    },
    [setVersioned]
  );

  const stageStepAt = useCallback(
    (stepIndex: number, transform: (step: StepConfiguration) => StepConfiguration) => {
      writeStepsVia(transformAt(stepIndex, transform));
    },
    [writeStepsVia]
  );

  const stageViewerSteps = useCallback(
    (update: StepsUpdater) => {
      writeStepsVia(resolveUpdater(update));
    },
    [writeStepsVia]
  );

  const applyProtocolNameFn = useCallback(
    ({ name }: Parameters<typeof applyProtocolName>[1]) => {
      setVersioned((prev) =>
        prev.name === name ? prev : makeVersionedSteps(prev.steps, prev, name)
      );
    },
    [setVersioned]
  );

  const applySavedStepsFn = useCallback(
    ({ steps, name }: Parameters<typeof applySavedSteps>[1]) => {
      writeStepsVia(() => steps, name);
      setBaselineAtom(steps ? structuredClone(steps) : null);
    },
    [writeStepsVia, setBaselineAtom]
  );

  const applyServerCriteriaToViewportFn = useCallback(
    ({
      stepIndex,
      viewportId,
      criteria,
      seriesSmid,
    }: Parameters<typeof applyServerCriteriaToViewport>[1]) => {
      const { stepsUpdate, baselineUpdate } = criteriaUpdatesFor({
        stepIndex,
        viewportId,
        criteria,
        seriesSmid,
      });
      writeStepsVia(stepsUpdate);
      setBaselineAtom(baselineUpdate);
    },
    [writeStepsVia, setBaselineAtom]
  );

  return {
    applyServerCriteriaToViewport: applyServerCriteriaToViewportFn,
    applySavedSteps: applySavedStepsFn,
    applyProtocolName: applyProtocolNameFn,
    stageViewerSteps,
    stageStepAt,
    selectStep: selectStepFn,
    selectStepWithIdentity: selectStepWithIdentityFn,
    selectSubStep: selectSubStepFn,
    resetStepIndices: resetStepIndicesFn,
  };
};
